import { zodResolver } from '@mantine/form';
import { useForm } from '../contexts/formContext';
import { CombinedSchema, OperationsFormValues } from '../schema';
import { Workflow, WorkflowConfig } from '@/app/workflows/actions/interfaces';
import { useEditedWorkflowActions, useEditedWorkflowState } from '@/app/workflows/contexts/editedWorkflows';
import { getBlankWorkflow } from '@/app/workflows/actions/constants/blankWorkflow';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { OperationId, operationIds } from '@/app/workflows/components/v6/operations/operations';
import { useGrowthbookContext, GrowthbookFeature } from '@/app/contexts/GrowthbookContext';
import { useWorkflow } from '../contexts/WorkflowContext';

export const useOperationsForm = (workflow: Workflow) => {
  const { clearedWorkflowIds } = useEditedWorkflowState();
  const { upsertEditedWorkflow } = useEditedWorkflowActions();
  const { setWorkflow } = useWorkflow();
  const [isReseting, setIsReseting] = useState(false);

  const growthbook = useGrowthbookContext();
  const features: GrowthbookFeature[] = growthbook?.isOn('show-uvunwrap') ? ['show-uvunwrap'] : [];

  const onValuesChange = useDebouncedCallback((values: OperationsFormValues) => {
    if (isReseting) {
      return setIsReseting(false);
    }

    const compressionAndExport = { ...values.compressionAndExport };
    const { filename } = compressionAndExport;
    delete compressionAndExport.filename;

    const enabledOperations = Object.entries(values)
      .filter(([_, value]) => !!value)
      .map(([key]) => key)
      .filter((o) => operationIds.has(o as OperationId)) as OperationId[];

    const config: WorkflowConfig = mapFormValuesToConfig(values);
    upsertEditedWorkflow(workflow.id);
    setWorkflow({
      ...workflow,
      config: {
        ...config,
        compressionAndExport,
        schema: getBlankWorkflow(features).config.schema,
      },
      meta: {
        ...workflow.meta,
        filename,
      },
      clientMetadata: {
        ...workflow.clientMetadata,
        enabledOperations: enabledOperations,
      },
    });
  }, 150);

  const config = workflow.config as WorkflowConfig;
  const form = useForm({
    initialValues: {
      id: workflow.id,
      ...config,
      limits: config.limits,
      targets: config.targets,
      assetSimplification: config.assetSimplification
        ? {
            ...config.assetSimplification,
            uvAndAtlasSettings: {
              ...config.assetSimplification.uvAndAtlasSettings,
              atlasingFactor: config.assetSimplification.uvAndAtlasSettings.atlasingFactor ?? 1, //Have no idea if this line is still needed, but seems to be introduced here: https://github.com/DGG3D/dgg-3d-platform/commit/4825d2edb56c7e1a238e2570469cd6d6c411bfc3
            },
          }
        : undefined,
      sceneManipulation: config.sceneManipulation,
      compressionAndExport: {
        ...config.compressionAndExport,
        filename: workflow.meta?.filename ?? {
          useId: false,
          usePresetName: false,
          useSuffix: '',
        },
      },
      // SCHEMA_VERSION (to find searching it later)
      schema: '2.13',
      uvUnwrapping: config.uvUnwrapping,
    },
    validate: zodResolver(CombinedSchema),
    onValuesChange,
  });

  useEffect(() => {
    if (clearedWorkflowIds?.includes(workflow.id)) {
      setIsReseting(true);
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearedWorkflowIds?.length]);

  return { form };
};

function mapFormValuesToConfig(values: OperationsFormValues): WorkflowConfig {
  return {
    limits: values.limits,
    targets: values.targets,
    assetSimplification: values.assetSimplification,
    uvUnwrapping: values.uvUnwrapping,
    schema: values.schema,
    sceneManipulation: values.sceneManipulation,
    compressionAndExport: values.compressionAndExport,
  };
}
